import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountDeletionResult } from '@up2d8/contracts';
import { Btn, Field, Form, Input, Note, btnClass } from '../admin-kit';
import { formatDate } from '../../shared/format';
import { useSession } from '../../shared/session';
import { AuthFrame, AuthIcon, AuthTitle, ClockIcon, DoneIcon, MailIcon } from './auth-frame';

/**
 * Account verwijderen zonder aanmelden: `/account-verwijderen` (DEL-1, ADR-0098).
 *
 * De app- en de Play-winkel eisen een webpagina waar iemand zijn account kan laten verwijderen,
 * ook als hij de app al weg deed. Twee toestanden op hetzelfde pad:
 *
 * - **zonder token**: een adres invullen. Het antwoord is altijd hetzelfde, zoals bij wachtwoord
 *   vergeten; anders verraadt deze pagina wie waar een account heeft;
 * - **met token** (de link uit de mail): bevestigen. Pas die klik registreert het verzoek.
 *
 * `/account-verwijderen/annuleren` is de link uit de bevestigingsmail.
 */

/** Het token uit de link, één keer gelezen en dan uit de adresbalk gehaald (zoals bij de reset). */
function useTokenFromUrl(): string {
  const search = useSearch({ strict: false }) as { token?: string };
  const [token] = useState(search.token ?? '');
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  }, []);
  return token;
}

/** Wat er gebeurt, in vier zinnen; staat zowel op het formulier als bij de bevestiging. */
function WhatHappens() {
  const { t } = useTranslation();
  return (
    <ul className="a-muted" style={{ paddingLeft: '1.25rem', listStyle: 'disc' }}>
      <li>{t('accountDeletion.steps.immediately')}</li>
      <li>{t('accountDeletion.steps.erased', { days: 30 })}</li>
      <li>{t('accountDeletion.steps.kept')}</li>
      <li>{t('accountDeletion.steps.cancelByMail')}</li>
    </ul>
  );
}

export function AccountDeletionPage() {
  const token = useTokenFromUrl();
  return token ? <ConfirmDeletion token={token} /> : <RequestDeletion />;
}

function RequestDeletion() {
  const { t } = useTranslation();
  const { api } = useSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.POST('/api/v1/account-deletion/request', { body: { email: email.trim() } });
    } finally {
      // Ook bij een fout: het scherm zegt hetzelfde, wat de server ook antwoordde.
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <AuthFrame>
        <AuthIcon tone="success" icon={<MailIcon />} />
        <AuthTitle>{t('accountDeletion.web.sentTitle')}</AuthTitle>
        <p className="a-muted" role="status">
          {t('accountDeletion.web.sentText')}
        </p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthTitle>{t('accountDeletion.title')}</AuthTitle>
      <p className="a-muted">{t('accountDeletion.web.intro')}</p>
      <WhatHappens />
      <Form onSubmit={() => void submit()}>
        <Field label={t('login.email')}>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          )}
        </Field>
        <Note tone="info">{t('accountDeletion.web.pinNote')}</Note>
        <Btn type="submit" size="lg" block disabled={busy} aria-busy={busy || undefined}>
          {t('accountDeletion.web.submit')}
        </Btn>
      </Form>
    </AuthFrame>
  );
}

function ConfirmDeletion({ token }: { token: string }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [state, setState] = useState<'ask' | 'done' | 'refused' | 'invalid'>('ask');
  const [result, setResult] = useState<AccountDeletionResult | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data, error } = await api.POST('/api/v1/account-deletion/confirm', {
        body: { token },
      });
      if (error || !data) {
        setState('invalid');
        return;
      }
      setResult(data as AccountDeletionResult);
      setState(data.refused.length > 0 ? 'refused' : 'done');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'invalid') {
    return (
      <AuthFrame>
        <AuthIcon tone="danger" icon={<ClockIcon />} />
        <AuthTitle>{t('accountDeletion.web.invalidTitle')}</AuthTitle>
        <p className="a-muted" role="alert">
          {t('accountDeletion.web.invalidText')}
        </p>
        <Link
          to="/account-verwijderen"
          className={btnClass({ variant: 'sec', size: 'lg', block: true })}
        >
          {t('accountDeletion.web.again')}
        </Link>
      </AuthFrame>
    );
  }

  if (state === 'refused') {
    return (
      <AuthFrame>
        <AuthIcon tone="danger" icon={<ClockIcon />} />
        <AuthTitle>{t('accountDeletion.web.refusedTitle')}</AuthTitle>
        <ul className="a-muted" role="alert" style={{ paddingLeft: '1.25rem', listStyle: 'disc' }}>
          {result?.refused.map((refusal) => (
            <li key={`${refusal.organisation}-${refusal.reason}`}>
              {refusal.reason === 'last_admin'
                ? t('accountDeletion.refusal.lastAdmin', { name: refusal.organisation })
                : t('accountDeletion.refusal.platformAdmin')}
            </li>
          ))}
        </ul>
        <p className="a-muted">{t('accountDeletion.web.refusedText')}</p>
      </AuthFrame>
    );
  }

  if (state === 'done') {
    return (
      <AuthFrame>
        <AuthIcon tone="success" icon={<DoneIcon />} />
        <AuthTitle>{t('accountDeletion.web.doneTitle')}</AuthTitle>
        <p className="a-muted" role="status">
          {t('accountDeletion.app.doneText', {
            date: result?.eraseAfter ? formatDate(result.eraseAfter) : '',
          })}
        </p>
        <p className="a-muted">{t('accountDeletion.app.doneMail')}</p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthTitle>{t('accountDeletion.web.confirmTitle')}</AuthTitle>
      <WhatHappens />
      <Btn
        variant="danger"
        size="lg"
        block
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={() => void confirm()}
      >
        {t('accountDeletion.web.confirm')}
      </Btn>
    </AuthFrame>
  );
}

/** De link uit de bevestigingsmail: het verzoek annuleren, binnen de bedenktijd. */
export function AccountDeletionCancelPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const token = useTokenFromUrl();
  const [state, setState] = useState<'ask' | 'done' | 'invalid'>(token ? 'ask' : 'invalid');
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    setBusy(true);
    try {
      const { error } = await api.POST('/api/v1/account-deletion/cancel', { body: { token } });
      setState(error ? 'invalid' : 'done');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'invalid') {
    return (
      <AuthFrame>
        <AuthIcon tone="danger" icon={<ClockIcon />} />
        <AuthTitle>{t('accountDeletion.cancel.invalidTitle')}</AuthTitle>
        <p className="a-muted" role="alert">
          {t('accountDeletion.cancel.invalidText')}
        </p>
      </AuthFrame>
    );
  }

  if (state === 'done') {
    return (
      <AuthFrame>
        <AuthIcon tone="success" icon={<DoneIcon />} />
        <AuthTitle>{t('accountDeletion.cancel.doneTitle')}</AuthTitle>
        <p className="a-muted" role="status">
          {t('accountDeletion.cancel.doneText')}
        </p>
        <Link to="/app/login" className={btnClass({ size: 'lg', block: true })}>
          {t('accountDeletion.cancel.toLogin')}
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthTitle>{t('accountDeletion.cancel.title')}</AuthTitle>
      <p className="a-muted">{t('accountDeletion.cancel.intro')}</p>
      <Btn
        size="lg"
        block
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={() => void cancel()}
      >
        {t('accountDeletion.cancel.submit')}
      </Btn>
    </AuthFrame>
  );
}
